import React, { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import logo from '../assets/images/Logo_new.svg'
import insta from '../assets/images/insta.svg'
import fb from '../assets/images/fb.svg'
import whatsapp from '../assets/images/whatsapp.svg'
import { Link } from 'react-scroll'

const Header = () => {
    const heroRef = useRef(null)
    const navRef = useRef(null)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        // Modern scroll-based navbar transformation
        const handleScroll = () => {
            const scrollPosition = window.scrollY
            setScrolled(scrollPosition > 50)
        }

        window.addEventListener('scroll', handleScroll)

        // Advanced GSAP animations
        const ctx = gsap.context(() => {
            // Hero section entrance animation
            const tl = gsap.timeline({ delay: 0.5 })
            
            tl.fromTo(".hero-title", 
                { opacity: 0, y: 100, scale: 0.8 },
                { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: "power3.out", stagger: 0.2 }
            )
            .fromTo(".hero-subtitle", 
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, "-=0.8"
            )
            .fromTo(".hero-buttons", 
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.8, ease: "back.out(1.7)" }, "-=0.6"
            )

            // Floating elements with modern parallax
            gsap.to(".floating-orb", {
                y: "random(-20, 20)",
                x: "random(-15, 15)",
                duration: "random(3, 6)",
                ease: "sine.inOut",
                yoyo: true,
                repeat: -1,
                stagger: 0.5
            })

            // Modern text reveal animation
            gsap.to(".text-reveal", {
                backgroundPosition: "200% 0",
                duration: 2,
                ease: "power2.inOut",
                repeat: -1,
                yoyo: true
            })

        }, heroRef)

        return () => {
            ctx.revert()
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    return (
        <div className="relative min-h-screen overflow-hidden">
            {/* Ultra-modern background with mesh gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-green-50/50 to-emerald-50">
                {/* Dynamic mesh pattern */}
                <div className="absolute inset-0 opacity-30">
                    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="mesh" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                <circle cx="50" cy="50" r="1" fill="currentColor" className="text-green-400"/>
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#mesh)" />
                    </svg>
                </div>
            </div>

            {/* Modern floating orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="floating-orb absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-full blur-xl"></div>
                <div className="floating-orb absolute top-40 right-20 w-48 h-48 bg-gradient-to-r from-emerald-400/15 to-teal-500/15 rounded-full blur-2xl"></div>
                <div className="floating-orb absolute bottom-32 left-1/4 w-24 h-24 bg-gradient-to-r from-green-500/25 to-lime-400/25 rounded-full blur-lg"></div>
                <div className="floating-orb absolute bottom-20 right-10 w-36 h-36 bg-gradient-to-r from-emerald-300/20 to-green-400/20 rounded-full blur-xl"></div>
            </div>

            {/* Ultra-modern Navigation */}
            <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled 
                    ? 'bg-white/80 backdrop-blur-2xl border-b border-gray-200/50 shadow-2xl' 
                    : 'bg-transparent'
            }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Modern Logo */}
                        <div className="flex-shrink-0 group">
                            <img 
                                src={logo} 
                                alt="Asian Harvest" 
                                className="h-14 w-auto transition-transform duration-300 group-hover:scale-105" 
                            />
                        </div>

                        {/* Modern Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-1">
                            {['about', 'products', 'contact'].map((item) => (
                                <Link 
                                    key={item}
                                    to={item} 
                                    spy={true} 
                                    smooth={true} 
                                    offset={-80} 
                                    duration={600}
                                    className="relative px-6 py-3 text-gray-700 hover:text-green-600 font-medium text-sm uppercase tracking-wider transition-all duration-300 cursor-pointer group"
                                >
                                    {item}
                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-600 to-emerald-600 transition-all duration-300 group-hover:w-full"></span>
                                </Link>
                            ))}
                            
                            {/* Modern Social Icons */}
                            <div className="flex items-center space-x-2 ml-8">
                                <a href="https://facebook.com" className="p-2 text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-110">
                                    <img src={fb} alt="Facebook" className="w-5 h-5" />
                                </a>
                                <a href="https://instagram.com/asian.harvest" className="p-2 text-gray-600 hover:text-pink-600 transition-all duration-300 hover:scale-110">
                                    <img src={insta} alt="Instagram" className="w-5 h-5" />
                                </a>
                                <a href="https://wa.me/9526750675" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-full transition-all duration-300 flex items-center space-x-2 hover:scale-105 shadow-lg hover:shadow-xl">
                                    <img src={whatsapp} alt="WhatsApp" className="w-4 h-4" />
                                    <span className="text-sm font-semibold">Chat Now</span>
                                </a>
                            </div>
                        </div>

                        {/* Modern Mobile menu button */}
                        <div className="md:hidden">
                            <button 
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="relative p-3 text-gray-700 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:ring-offset-2 rounded-lg transition-all duration-300"
                            >
                                <div className="w-6 h-6 relative">
                                    <span className={`block absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-2.5' : '-translate-y-1'}`}></span>
                                    <span className={`block absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? 'opacity-0' : 'translate-y-1'}`}></span>
                                    <span className={`block absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : 'translate-y-3'}`}></span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Ultra-modern Mobile Navigation */}
                    <div className={`md:hidden transition-all duration-500 ease-in-out ${
                        isMenuOpen 
                            ? 'max-h-96 opacity-100' 
                            : 'max-h-0 opacity-0 pointer-events-none'
                    }`}>
                        <div className="bg-white/90 backdrop-blur-xl rounded-2xl m-4 p-6 shadow-2xl border border-gray-200/50">
                            <div className="space-y-4">
                                {['about', 'products', 'contact'].map((item) => (
                                    <Link 
                                        key={item}
                                        to={item} 
                                        spy={true} 
                                        smooth={true} 
                                        offset={-80} 
                                        duration={600}
                                        className="block px-4 py-3 text-gray-700 hover:text-green-600 font-medium uppercase tracking-wider transition-all duration-300 cursor-pointer hover:bg-green-50 rounded-lg"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        {item}
                                    </Link>
                                ))}
                                <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-200">
                                    <a href="https://facebook.com" className="p-2 text-gray-600 hover:text-blue-600 transition-all duration-300">
                                        <img src={fb} alt="Facebook" className="w-6 h-6" />
                                    </a>
                                    <a href="https://instagram.com/asian.harvest" className="p-2 text-gray-600 hover:text-pink-600 transition-all duration-300">
                                        <img src={insta} alt="Instagram" className="w-6 h-6" />
                                    </a>
                                    <a href="https://wa.me/9526750675" className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105">
                                        WhatsApp
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Ultra-Modern Hero Section */}
            <div ref={heroRef} className="relative pt-24 pb-20 flex items-center justify-center min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    {/* Modern Badge */}
                    <div className="inline-flex items-center px-6 py-3 mb-8 bg-white/80 backdrop-blur-xl rounded-full shadow-lg border border-gray-200/50">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></span>
                        <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Premium Quality • Export Grade</span>
                    </div>

                    {/* Hero Title with Modern Typography */}
                    <h1 className="hero-title mb-8">
                        <div className="text-6xl md:text-8xl font-black text-gray-900 leading-tight mb-4">
                            <span className="inline-block">Premium</span>
                        </div>
                        <div className="text-6xl md:text-8xl font-black leading-tight">
                            <span className="text-reveal bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 bg-clip-text text-transparent bg-[length:200%_100%]">
                                Asian Harvest
                            </span>
                        </div>
                    </h1>

                    {/* Modern Subtitle */}
                    <p className="hero-subtitle text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed font-medium">
                        From the <span className="text-green-600 font-bold">cashew capital of the world</span>, delivering premium quality nuts and 
                        <span className="text-green-600 font-bold"> pure saffron</span> to your doorstep with unmatched freshness and authenticity
                    </p>
                    
                    {/* Modern Action Buttons */}
                    <div className="hero-buttons flex flex-col sm:flex-row gap-6 justify-center items-center mb-20">
                        <Link to="products" spy={true} smooth={true} offset={-80} duration={600}>
                            <button className="group relative px-10 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-lg rounded-2xl transition-all duration-300 hover:from-green-700 hover:to-emerald-700 hover:scale-105 hover:shadow-2xl cursor-pointer overflow-hidden">
                                <span className="relative z-10">Explore Products</span>
                                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                            </button>
                        </Link>
                        <Link to="about" spy={true} smooth={true} offset={-80} duration={600}>
                            <button className="px-10 py-4 border-2 border-gray-900 text-gray-900 font-bold text-lg rounded-2xl hover:bg-gray-900 hover:text-white transition-all duration-300 hover:scale-105 cursor-pointer backdrop-blur-sm">
                                Our Story
                            </button>
                        </Link>
                    </div>

                    {/* Modern Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
                        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-gray-200/50 hover:scale-105 transition-all duration-300">
                            <div className="text-3xl font-black text-green-600 mb-2">100%</div>
                            <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Pure & Natural</div>
                        </div>
                        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-gray-200/50 hover:scale-105 transition-all duration-300">
                            <div className="text-3xl font-black text-green-600 mb-2">Export</div>
                            <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Quality Grade</div>
                        </div>
                        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-gray-200/50 hover:scale-105 transition-all duration-300">
                            <div className="text-3xl font-black text-green-600 mb-2">Kerala</div>
                            <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Direct Source</div>
                        </div>
                    </div>

                    {/* Modern Scroll Indicator */}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                        <div className="flex flex-col items-center space-y-2 text-gray-600">
                            <span className="text-xs font-medium uppercase tracking-wider">Scroll to explore</span>
                            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
                                <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-bounce"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Header